import { Component, EventEmitter, Output, computed, inject, Input, OnInit, signal, ChangeDetectorRef, PLATFORM_ID } from '@angular/core';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { ApiService } from '../../../../services/api.service';
import { IComCategoryItem } from '../../../../models/IComCategoryItem';
import { IComCategoryOptionsResponse } from '../../../../models/IComCategoryOptionsResponse';
import { DomSanitizer } from '@angular/platform-browser';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../../services/authentication/auth.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { environment } from '../../../../../environments/environment';
import { distinctUntilChanged, Subscription, Subject } from 'rxjs';
import { faMagnifyingGlass, faX } from '@fortawesome/free-solid-svg-icons';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastMessageService } from '../../../../services/toast-message/toast-message.service';
import { HttpErrorResponse } from '@angular/common/http';
import { PaginatorModule } from 'primeng/paginator';
import { MainItemsComponent } from './main-items/main-items.component';
import { PaginatorStateService } from '../../../../services/paginator/paginator-state.service';
import { SelectButton } from 'primeng/selectbutton';
import { SelectButtonModule } from 'primeng/selectbutton';
import { faImage, faList, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { MenuItem } from 'primeng/api';
import { SelectModule } from 'primeng/select';
import { MultiSelectChangeEvent, MultiSelectModule } from 'primeng/multiselect';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { SliderModule } from 'primeng/slider';
import { Slider, SliderSlideEndEvent } from 'primeng/slider';
import { formatCurrency, getCurrencySymbol } from '@angular/common'
import { isPlatformBrowser } from '@angular/common';

enum SortTypes {
  Name = "item_name_sort",
  CurrentBid = "item_current_bid",
  Value = "item_value_sort",
  NumberBids = "item_number_bids",
  CloseDate = "item_close_date"
}

interface ISubCategories {
  name: string;
  code: string;
}

interface ILocations {
  name: string;
  code: string;
}

interface IPrices {
  sliderTick: string;
  originalString: string;
  count: number;
  id: number;
}

interface IBuyNow {
  name: string;
  code: string;
}
interface IOnlineNow {
  name: string;
  code: string;
}
interface IClosingSoon {
  name: string;
  code: string;
}
interface INew {
  name: string;
  code: string;
}


@Component({
  selector: 'app-main-item-list',
  standalone: true,
  imports: [RouterModule,
    CommonModule,
    IconFieldModule,
    InputIconModule,
    FontAwesomeModule,
    ProgressSpinnerModule,
    PaginatorModule,
    MainItemsComponent,
    SelectButton,
    SelectButtonModule,
    FormsModule,
    ButtonModule,
    BreadcrumbModule,
    SelectModule,
    MultiSelectModule,
    DialogModule,
    CheckboxModule,
    SliderModule,
    Slider],
  templateUrl: './main-item-list.component.html',
  styleUrl: './main-item-list.component.scss'
})
export class MainItemListComponent implements OnInit {
  @Output() openModal = new EventEmitter<number>();
  @Input() category?: string;
  private platformId = inject(PLATFORM_ID);
  validCategories: string[] = ["closing", "dining", "designer", "electronics", "tickets", "travel", "other", "experiences"];
  private readonly apiService = inject(ApiService);
  private authService = inject(AuthService);
  oidcSecurityService = inject(OidcSecurityService);
  private readonly messageService = inject(ToastMessageService);
  paginatorService = inject(PaginatorStateService);
  private readonly cdr = inject(ChangeDetectorRef);
  isFavorited: boolean | null = null;
  isAuthenticated: boolean = false;
  authSubscription!: Subscription;
  itemList: IComCategoryItem[] = [];
  totalItems: number = 0;
  faMagnifyingGlass = faMagnifyingGlass;
  faX = faX;
  readonly contentIsFetched = signal(false);
  pageIsLoaded = computed(() => this.contentIsFetched());
  selectedSort: string = 'NAME_ASC'; // set the initial sort
  selectedLowerBoundSignal = computed(() => this.getSelectedLowerBound());
  selectedUpperBoundSignal = computed(() => this.getSelectedUpperBound());
  deepLinkSubcategory: string | undefined = undefined;
  // paginator
  page: number = 0;
  rowsPerPage: number = 30;
  previousRows: number = this.rowsPerPage;
  rowsPerPageOptions = [15, 30, 60, 90]; // 10, 15, 30, 50, 90
  first = signal(0);
  rows = signal(this.rowsPerPage);
  selectedSubCategories: string[] = [];

  sortFilters = [
    { label: 'Item Name - Ascending', value: 'NAME_ASC' },
    { label: 'Item Name - Descending', value: 'NAME_DESC' },
    { label: 'Value - Ascending', value: 'VALUE_ASC' },
    { label: 'Value- Descending', value: 'VALUE_DESC' },
    { label: 'Leading Bid - Ascending', value: 'HIGHBID_ASC' },
    { label: 'Leading Bid - Descending', value: 'HIGHBID_DESC' },
    { label: 'Number Bids - Ascending', value: 'NUMBERBIDS_ASC' },
    { label: 'Number Bids - Descending', value: 'NUMBERBIDS_DESC' },
    { label: 'Time Left - Ascending', value: 'TIMELEFT_ASC' },
    { label: 'Time Left - Descending', value: 'TIMELEFT_DESC' }
  ];

  // Signal store filters
  filters = signal({
    attributes: [] as number[],
    locations: [] as number[],
    prices: [] as number[],
    sort: SortTypes.Name.toString(),
    isDesc: false,
    page: 1,
    per: this.rowsPerPage,
    isOnlyNew: false,
    isOnlyClosingSoon: false,
    isOnlyOnline: false,
    isOnlyBuyNow: false
  });

  subCategories = signal<ISubCategories[]>([]);
  locations = signal<ILocations[]>([]);
  prices = signal<IPrices[]>([]);
  buyNow = signal<IBuyNow | null>(null);
  onlineNow = signal<IOnlineNow | null>(null);
  closingSoon = signal<IClosingSoon | null>(null);
  newItems = signal<INew | null>(null);

  filterDialogVisible = false;
  // Slider indexes [leftIndex, rightIndex]
  priceRangeIndexes = signal<[number, number]>([0, 0]);
  cachedPriceRangeIndexes = [...this.priceRangeIndexes()];
  // checkbox states for locations (parallel array)
  selectedLocations: boolean[] = [];
  cachedSelectedLocations = [...this.selectedLocations];

  // Additional options checkbox states
  selectedBuyNow = false;
  selectedOnlineNow = false;
  selectedClosingSoon = false;
  selectedNewItems = false;

  viewMode: 'image' | 'list' = 'image';
  protected icons = {
    faImage: faImage,
    faList: faList
  }

  faTrash = faTrash;

  // breadcrumb
  itemsBreadcrumb: MenuItem[] = [];

  categoryOptions: IComCategoryOptionsResponse = {
    attributes: [],
    locations: [],
    prices: [],
    showBuyNow: { id: 0, name: "" },
    showOnlineOnly: { id: 0, name: "" },
    showClosingSoon: { id: 0, name: "" },
    showNew: { id: 0, name: "" }
  };

  itemListOptions: any[] = [{ label: 'Image View', value: 'image' }, { label: 'List View', value: 'list' }];
  firstSignal = signal(0);
  // It is automatically recalculated when a change occurs on the page.
  pagination = this.paginatorService.getPagination();

  constructor(private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
  ) {

    this.route.paramMap.subscribe(params => {

      // This code here makes it so that when we change navigation routes the onInit is called again
      // otherwise the component is cached and init is not called and therefore nothing changes on the page
      const slug = params.get('category');
      if ((slug !== undefined) && (slug !== null)) {
        this.category = slug.toString();
        // if category is not one of accepted values, redirect to 404 page
        if ((this.category === undefined) ||
          (this.category !== undefined && !this.validCategories.includes(this.category.toLowerCase()))) {
          console.log('Page not found');
          this.router.navigate(['/404']);
        } else {
          // FYI - To Force an error and test error handling: this.loadCategoryItems('__force_error__');

          // determine if there is a Deep link to a subcategory
          this.route.queryParams.subscribe(params => {
            this.deepLinkSubcategory = params['subcat'];
            // If deeplinked to a subcategory, we need to set the filters before calling loadCategoryItems
            if (this.category !== undefined) {
              this.resetFilters();
              if (this.deepLinkSubcategory !== undefined) {
                this.applyDeepLinkFiltersAndLoadItems(this.category.toLowerCase(), this.deepLinkSubcategory);
              }
              else {
                this.loadCategoryItems(this.category.toLowerCase(), true);
              }
            }
          });
        }
      }
    });
  }


  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return; // avoid SSR/hydration flicker

    // Set view mode - note: copied this logic from item-list component
    this.viewMode = 'image';
    this.selectedSort = 'NAME_ASC'; // set the initial sort

    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('resize', () => {
        this.viewMode = window.innerWidth < 1200
          ? 'image'
          : (this.viewMode ?? 'image');
      });
    }


    this.authSubscription = this.authService.isAuthenticated$.pipe(distinctUntilChanged()).subscribe(isAuth => {

      // only fire the reload of items if the auth status has changed
      // otherwise we will fire it on contructor AND in OnInit on first page load
      if (isAuth) {
        if (this.isAuthenticated === false) {
          // Force a reload of the items
          this.isAuthenticated = true;
          if (this.category !== undefined) {
            //console.log('on onit route hit');
            this.loadCategoryItems(this.category.toLowerCase());
          }
        }
        this.isAuthenticated = true; // set for good measure

      } else {
        if (this.isAuthenticated === true) {
          // Force a reload of the items
          this.isAuthenticated = false;
          if (this.category !== undefined) {
            //console.log('on onit route hit');
            this.loadCategoryItems(this.category.toLowerCase());
          }
        }
        this.isAuthenticated = false; // set for good measure
      }
    });
  }

  private applyDeepLinkFiltersAndLoadItems(category: string, deeplinkedSubcategory: string | undefined) {
    // Create a copy of filters without sort, isDesc, page, per
    const { sort, isDesc, page, per, ...optionsFilters } = this.filters();

    try {
      this.apiService.getCategoryOptions(category, optionsFilters)
        .subscribe(
          itemResult => {
            this.categoryOptions = itemResult;

            // We only need to get and set subcategories since that is the only deep link we are supporting right now
            if (this.categoryOptions === null) {
              this.subCategories.set([]);
            } else {
              const subCategories: ISubCategories[] = [];

              // Add some whitespace to the end of the name to push the chevron to the right
              if (this.categoryOptions.attributes !== null) {
                for (const opt of this.categoryOptions.attributes) {
                  subCategories.push({
                    name: `${opt.name ?? ""} (${opt.count ?? 0})\u00A0\u00A0\u00A0\u00A0\u00A0`,
                    code: String(opt.id ?? "")
                  });
                }
                // console.log('subcategories', subCategories);
                this.subCategories.set(subCategories);

                // Check if we are deep linked to a subcategory and if so, select it
                if (deeplinkedSubcategory !== undefined) {
                  // find the matching name in subcategories that matches deepLinkSubcategory (case-insensitive)
                  const matchingSubcategory = subCategories.find(sub =>
                    sub.name.toLowerCase().includes(this.deepLinkSubcategory!.toLowerCase())
                  );

                  if (matchingSubcategory) {
                    const matchingCode = matchingSubcategory.code;
                    const matchingNumber = Number(matchingCode);

                    // (1) Update the filters with the matching attribute number
                    const newAttributes: number[] = [matchingNumber];
                    this.filters.update(filters => {
                      const updatedFilters = {
                        ...filters,
                        attributes: newAttributes
                      };
                      this.paginatorService.setCategoryFilters(updatedFilters); // cache it locally
                      return updatedFilters;
                    });

                    // (2) Set the UI selection to the matching code
                    this.selectedSubCategories = [matchingCode];
                  }

                  this.deepLinkSubcategory = undefined; // reset the deep link so it won't override future multi-select selections
                }

              } else {
                this.subCategories.set([]);
              }
              // Load the items now
              if (this.category !== undefined) {
                this.loadCategoryItems(this.category.toLowerCase(), true);
              }
            }
          },
          (error: any) => {
            // Extract the error message and show error as Toast
            const errorMsg = this.buildErrorMessage(error);
            this.messageService.error(errorMsg || 'Unknown error');
          });

    } catch (e) {
      console.log(e);

      // Show error as Toast
      const errorMsg =
        e instanceof Error && e.message
          ? e.message
          : 'An unexpected error occurred. Please try again later.';

      this.messageService.error(errorMsg);
    }
  }

  private loadCategoryItems(category: string, resetFilters: boolean = false, pricesChanged: boolean = false) {

    try {
      this.contentIsFetched.set(false);
      this.apiService.getCategoryItems(category, this.filters())
        .subscribe(
          itemResult => {
            this.contentIsFetched.set(true); // note: set this to true so spinner stops spinning

            // console.log(itemResult);
            if (itemResult && itemResult.items !== null) {
              this.totalItems = itemResult.total;
              // Format Breadcrumbs
              if (this.haveFilteredResults()) {
                this.itemsBreadcrumb = [
                  { label: 'BiddingForGood', routerLink: `/` },
                  { label: ((this.category !== undefined) ? this.selectedCategory() : ''), routerLink: `/items/${this.category}` },
                  { label: 'Filter' + ' (' + this.totalItems + ' results)' }
                ];
              } else {
                this.itemsBreadcrumb = [
                  { label: 'BiddingForGood', routerLink: `/` },
                  {
                    label: ((this.category !== undefined) ? this.selectedCategory() : '') +
                      ' (' +
                      this.totalItems +
                      ' results)',
                    routerLink: `/items/${this.category}`
                  }
                ];
              }
              this.itemList = itemResult.items.map(item => ({
                ...item,
                imageUrl: item.imageUrl?.trim().toLowerCase() !== 'null' && item.imageUrl?.trim()
                  ? `${item.imageUrl.trim()}`
                  : '/item-default.jpg',
              }));

              // Load options for further filtering
              this.loadCategoryOptions(category, resetFilters, pricesChanged);

              return this.itemList;

            } else {
              this.itemList = [];
              this.totalItems = 0;
              return this.itemList;
            }
          },
          (error: any) => {
            this.contentIsFetched.set(true); // note: set this to true so spinner stops spinning
            // Extract the error message and show error as Toast
            const errorMsg = this.buildErrorMessage(error);
            this.messageService.error(errorMsg || 'Unknown error');
          });

    } catch (e) {
      console.log(e);
      this.contentIsFetched.set(true); // note: set this to true so spinner stops spinning

      // Show error as Toast
      const errorMsg =
        e instanceof Error && e.message
          ? e.message
          : 'An unexpected error occurred. Please try again later.';

      this.messageService.error(errorMsg);
    }
  }

  private loadCategoryOptions(category: string, resetFilters: boolean = false, pricesChanged: boolean = false) {
    // Create a copy of filters without sort, isDesc, page, per
    const { sort, isDesc, page, per, ...optionsFilters } = this.filters();

    try {
      this.apiService.getCategoryOptions(category, optionsFilters)
        .subscribe(
          itemResult => {
            this.categoryOptions = itemResult;

            // trigger a rebinding with the categories multiselect
            // loop through attributes and fill categories for UI
            if (this.categoryOptions === null) {
              this.subCategories.set([]);
            } else {
              const subCategories: ISubCategories[] = [];
              const locations: ILocations[] = [];
              var originalPriceArray = this.prices(); // save a copy of the original prices

              // Add some whitespace to the end of the name to push the chevron to the right
              if (this.categoryOptions.attributes !== null) {
                for (const opt of this.categoryOptions.attributes) {
                  subCategories.push({
                    name: `${opt.name ?? ""} (${opt.count ?? 0})\u00A0\u00A0\u00A0\u00A0\u00A0`,
                    code: String(opt.id ?? "")
                  });
                }
                // console.log('subcategories', subCategories);
                this.subCategories.set(subCategories);
              } else {
                this.subCategories.set([]);
              }

              if (this.categoryOptions.locations !== null) {
                for (const opt of this.categoryOptions.locations) {
                  // console.log(opt.id, opt.name, opt.count);
                  locations.push({
                    name: `${opt.name ?? ""} (${opt.count ?? 0})`,
                    code: String(opt.id ?? "")
                  });
                }
                this.locations.set(locations);
              } else {
                this.locations.set([]);
              }

              if ((this.categoryOptions.showBuyNow !== undefined) && (this.categoryOptions.showBuyNow !== null)) {
                this.buyNow.set({
                  name: `${this.categoryOptions.showBuyNow.name ?? ""} (${this.categoryOptions.showBuyNow.count ?? 0})`,
                  code: this.categoryOptions.showBuyNow.count !== undefined ? this.categoryOptions.showBuyNow.count.toString() : '0'
                });
              } else {
                this.buyNow.set(null);
                this.selectedBuyNow = false;
              }

              if ((this.categoryOptions.showOnlineOnly !== undefined) && (this.categoryOptions.showOnlineOnly !== null)) {
                this.onlineNow.set({
                  name: `${this.categoryOptions.showOnlineOnly.name ?? ""} (${this.categoryOptions.showOnlineOnly.count ?? 0})`,
                  code: this.categoryOptions.showOnlineOnly.count !== undefined ? this.categoryOptions.showOnlineOnly.count.toString() : '0'
                });
              } else {
                this.onlineNow.set(null);
                this.selectedOnlineNow = false;
              }

              if ((this.categoryOptions.showClosingSoon !== undefined) && (this.categoryOptions.showClosingSoon !== null)) {
                this.closingSoon.set({
                  name: `${this.categoryOptions.showClosingSoon.name ?? ""} (${this.categoryOptions.showClosingSoon.count ?? 0})`,
                  code: this.categoryOptions.showClosingSoon.count !== undefined ? this.categoryOptions.showClosingSoon.count.toString() : '0'
                });
              } else {
                this.closingSoon.set(null);
                this.selectedClosingSoon = false;
              }

              if ((this.categoryOptions.showNew !== undefined) && (this.categoryOptions.showNew !== null)) {
                this.newItems.set({
                  name: `${this.categoryOptions.showNew.name ?? ""} (${this.categoryOptions.showNew.count ?? 0})`,
                  code: this.categoryOptions.showNew.count !== undefined ? this.categoryOptions.showNew.count.toString() : '0'
                });
              } else {
                this.newItems.set(null);
                this.selectedNewItems = false;
              }

              if (resetFilters) {

                // reset all things resettable in modal
                this.setupPriceSlider(); // Set Prices Slider ticks
                this.initializePriceRange(); // Set Prices range to max and Prices range text boxes
                this.initializeLocationSelection(); // Reset to all locations available

              } else {

                if (!pricesChanged) {
                  // If prices did not change, update the price slider ticks and text boxes
                  this.setupPriceSlider(); // Set Prices Slider ticks
                  //  Now the tricky part, try to map the current price range to the new slider ticks as best as possible!
                  this.mapPreviousPriceRangeToNewSliderRanges(originalPriceArray); // Set Prices range to max and Prices range text boxes
                }

                // make sure to map selected locations to NEW locations
                // this is because depending on the filter criteria locations can come and go
                this.initializeLocationSelection(); // Reset to all locations available

                // Set selectedLocations to true for any locations that match optionsFilters.locations
                const locs = this.locations();
                const optionsFilters = this.filters();
                for (const locationId of optionsFilters.locations) {
                  const index = locs.findIndex(loc => loc.code === String(locationId));
                  if (index !== -1) {
                    this.selectedLocations[index] = true;
                  }
                }
              }
            }
          },
          (error: any) => {
            // Extract the error message and show error as Toast
            const errorMsg = this.buildErrorMessage(error);
            this.messageService.error(errorMsg || 'Unknown error');
          });

    } catch (e) {
      console.log(e);

      // Show error as Toast
      const errorMsg =
        e instanceof Error && e.message
          ? e.message
          : 'An unexpected error occurred. Please try again later.';

      this.messageService.error(errorMsg);
    }
  }

  setupPriceSlider() {
    // prices are special - we need to parse them for the UI slider control
    // the scenaios are:
    // 1. No prices at all - set array to nothing so no slider appears in the UI
    // 2. First price range is a phrase like "Less than $50"
    //    In this case we create ZERO as the first tick and the number from the phrase as the second tick
    //    All other ranges will then use the rightmost value for the next slider tick
    // 3. First price range is a normal range like "$101 - $200"
    //    In this case we create the first tick and second tick from the range
    // 4. First price range is a phrase like "More than $5000"
    //    In this case we create the first tick as the number from the phrase and the second tick will be the same number with a "+" sign
    //    This should always be the last tick on the slider and the slider will only have 2 ticks
    // 5. Last price range is a phrase like "More than $5000"
    //    In this case we extract the number and add it as the last tick with a "+" sign
    // 6. Any other price range in between is a normal range like "$101 - $200"
    //    In this case we use the rightmost value for the next slider tick
    var firstTickEstablished = false;
    const prices: IPrices[] = [];

    // Scenario #1 -Check if there are ANY prices
    if (this.categoryOptions.prices !== null) {
      //  sort by ID ascending, because ORDER matters
      for (const opt of this.categoryOptions.prices.sort((a, b) => a.id! - b.id!)) {
        // console.log(opt.id, opt.name, opt.count);

        let input = opt.name; // example: "$101 - 200" or "Less than $50"

        let lowerBound = "";
        let upperBound = "";
        if (input !== undefined && input.includes("-")) {
          // We are dealing with a RANGE - first split around the dash to get both lower/upper bounds
          const parts = input.split("-");

          lowerBound = parts[0].trim(); // left tick
          upperBound = this.formatToDollar(parts[1].trim()); // right tick

          if (firstTickEstablished === false) {
            // Scenario #3 - First tick is a RANGE
            prices.push({
              sliderTick: lowerBound,
              originalString: input,
              count: opt.count ?? 0, // not used but might as well collect it
              id: opt.id ?? 0
            });
            prices.push({
              sliderTick: upperBound,
              originalString: input,
              count: opt.count ?? 0, // not used but might as well collect it
              id: opt.id ?? 0
            });
            firstTickEstablished = true;
          } else {
            // Scenario 6 - any RANGE above the first tick on slider uses rightmost value for next slider tick
            prices.push({
              sliderTick: upperBound,
              originalString: input,
              count: opt.count ?? 0, // not used but might as well collect it
              id: opt.id ?? 0
            });
          }

        } else {
          // no dash means No Range is given,we are dealing with a phrase with a single number
          // extract the number first
          const numberMatch = (input !== undefined) ? (input.match(/\d+/g)?.map(Number) || []) : '0';

          if (input !== undefined && input.toLowerCase().includes("less than")) {
            // Scenario #2 - First entry is Less Than phrase like "Less than $50"
            upperBound = (Number(numberMatch)).toString();

            firstTickEstablished = true;
            prices.push({
              sliderTick: this.formatToDollar(0), // ZERO for first tick,
              originalString: input,
              count: opt.count ?? 0, // not used but might as well collect it
              id: opt.id ?? 0
            });

            prices.push({
              sliderTick: this.formatToDollar(upperBound), // User number of Less Than phrase as next tick value
              originalString: input,
              count: opt.count ?? 0, // not used but might as well collect it
              id: opt.id ?? 0
            });

          } else if (input !== undefined && input.toLowerCase().includes("more than")) {

            if (firstTickEstablished === false) {
              // Scenario #4 - FIRST entry is a phrase like "More than $500"
              lowerBound = (this.formatToDollar(Number(numberMatch))).toString();
              prices.push({
                sliderTick: lowerBound,
                originalString: input,
                count: opt.count ?? 0, // not used but might as well collect it
                id: opt.id ?? 0
              });

              var finalTick = (this.formatToDollar(Number(numberMatch))).toString() + '+';
              prices.push({
                sliderTick: finalTick,
                originalString: input,
                count: opt.count ?? 0, // not used but might as well collect it
                id: opt.id ?? 0
              });

              firstTickEstablished = true;
            } else {
              // Scenario #5 - LAST entry is a phrase like "More than $500"
              var finalTick = (this.formatToDollar(Number(numberMatch))).toString() + '+';

              prices.push({
                sliderTick: finalTick,
                originalString: input,
                count: opt.count ?? 0, // not used but might as well collect it
                id: opt.id ?? 0
              });
            }
          }
        }
      }
      this.prices.set(prices);
    } else {
      this.prices.set([]);
    }
  }

  formatToDollar(value: number | string): string {
    // Convert string input to number by removing $ and commas
    let numericValue: number;

    if (typeof value === 'string') {
      // Remove dollar signs, commas, and any whitespace
      const cleanedValue = value.replace(/[$,\s]/g, '');
      numericValue = parseFloat(cleanedValue);

      // Handle invalid input
      if (isNaN(numericValue)) {
        numericValue = 0;
      }
    } else {
      numericValue = value;
    }

    // 'en-US' for United States English locale
    // 'USD' for US Dollar currency code
    // '$' for the currency symbol (optional, can be inferred from currencyCode and locale)
    // '1.0' for digitsInfo (minIntegerDigits.minFractionDigits-maxFractionDigits) - no decimal places
    return formatCurrency(numericValue, 'en-US', getCurrencySymbol('USD', 'narrow', 'en-US'), 'USD', '1.0');
  }

  onOpenModalVertical(id: number) {
    this.openModal.emit(id);
    // console.log(id);
  }

  private buildErrorMessage(error: any): string {
    if (error instanceof HttpErrorResponse) {
      // Server returned an HTTP error
      // Prefer a server-side message if present
      const serverMsg =
        (error.error && (error.error.message || error.error.error || error.error.title)) ||
        error.message;

      if (error.status === 0) {
        return 'Unable to reach the server. Please check your connection.';
      }

      if (error.status >= 500) {
        return 'An unexpected error occurred. Please try again later.';
      }

      if (error.status === 404) {
        return 'No items were found for this category.';
      }

      return serverMsg || `Request failed (${error.status} ${error.statusText || ''}).`;
    }

    // Non-HTTP error (coding error, etc.)
    if (error instanceof Error && error.message) {
      return error.message;
    }

    return 'An unexpected error occurred. Please try again later.';
  }

  onPageChange(event: any) {
    this.filters.update(filters => ({
      ...filters,
      page: event.page + 1,
      per: event.rows
    }));

    this.firstSignal.set((this.previousRows !== event.rows) ? 1 : event.first);

    this.paginatorService.setPagination(event.page + 1, event.rows);

    this.pagination = {
      page: (this.previousRows !== event.rows) ? 1 : event.page + 1,
      per: event.rows
    };

    this.previousRows = event.rows;
    this.rowsPerPage = event.rows; // Save the current rows per page selection

    if (this.category !== undefined) {
      this.loadCategoryItems(this.category.toLowerCase());
    }
  }

  haveFilteredResults(): boolean {
    if (this.filters().attributes.length > 0 ||
      this.filters().locations.length > 0 ||
      this.filters().prices.length > 0 ||
      this.filters().isOnlyNew ||
      this.filters().isOnlyClosingSoon ||
      this.filters().isOnlyOnline ||
      this.filters().isOnlyBuyNow) {
      return true;
    };
    return false;
  };

  selectedCategory() {

    switch (this.category) {
      case "closing":
        return "Closing";
      case "dining":
        return "Dining";
      case "designer":
        return "Designer";
      case "electronics":
        return "Electronics";
      case "tickets":
        return "Tickets";
      case "travel":
        return "Travel";
      case "other":
        return "Other";
      case "experiences":
        return "Unique";

      default:
        return "Other"; // should never get hit
    }

  }

  resetModalFiltersOnly() {

    this.filters.update(filters => {
      const updatedFilters = {
        ...filters,
        locations: [] as number[],
        prices: [] as number[],
        isOnlyNew: false,
        isOnlyClosingSoon: false,
        isOnlyOnline: false,
        isOnlyBuyNow: false
      };
      return updatedFilters;
    });

    this.priceRangeIndexes.set([0, 0]);
    this.cachedPriceRangeIndexes = [...this.priceRangeIndexes()];
    this.selectedLocations = [];
    this.cachedSelectedLocations = [...this.selectedLocations];

    this.selectedBuyNow = false;
    this.selectedOnlineNow = false;
    this.selectedClosingSoon = false;
    this.selectedNewItems = false;

    if (this.category !== undefined) {
      this.loadCategoryItems(this.category.toLowerCase(), true);
    }

  }

  resetFilters() {
    this.filters.set({
      attributes: [] as number[],
      locations: [] as number[],
      prices: [] as number[],
      sort: SortTypes.Name.toString(),
      isDesc: false,
      page: 1,
      per: this.rowsPerPage,
      isOnlyNew: false,
      isOnlyClosingSoon: false,
      isOnlyOnline: false,
      isOnlyBuyNow: false
    });
    this.priceRangeIndexes.set([0, 0]);
    this.cachedPriceRangeIndexes = [...this.priceRangeIndexes()];
    this.selectedLocations = [];
    this.cachedSelectedLocations = [...this.selectedLocations];
  }

  mapSortName(name: string): string {

    switch (name) {
      case "NAME_ASC":
      case "NAME_DESC":
        return "item_name_sort";

      case "VALUE_ASC":
      case "VALUE_DESC":
        return "item_value_sort";

      case "HIGHBID_ASC":
      case "HIGHBID_DESC":
        return "item_current_bid";

      case "NUMBERBIDS_ASC":
      case "NUMBERBIDS_DESC":
        return "item_number_bids";

      case "TIMELEFT_ASC":
      case "TIMELEFT_DESC":
        return "item_close_date";

      default:
        return "item_name_sort"; // should never get hit
    }
  }

  onSortChange(event: any): void {
    this.selectedSort = event.value;


    this.filters.update(filters => {
      const updatedFilters = {
        ...filters,
        sort: this.mapSortName(event.value),
        isDesc: event.value.endsWith('DESC'),
        page: 1,
      };
      this.paginatorService.setCategoryFilters(updatedFilters);

      const first = (updatedFilters.page - 1) * updatedFilters.per;
      this.firstSignal.set(first);
      return updatedFilters;

    });

    if (this.category !== undefined) {
      this.loadCategoryItems(this.category.toLowerCase());
    }
  }

  clearFilters(): void {

    this.resetFilters();
    this.firstSignal.set(0); // reset paging to first page
    this.paginatorService.setPagination(1, this.rowsPerPage);
    this.paginatorService.clearCategoryFilters();
    this.selectedSubCategories = [];
    this.selectedSort = 'NAME_ASC'; // set the initial sort
    this.cdr.detectChanges(); // needed this to force the sub categories selectbox to reset its contents completely
    this.selectedBuyNow = false;
    this.selectedOnlineNow = false;
    this.selectedClosingSoon = false;
    this.selectedNewItems = false;

    if (this.category !== undefined) {
      this.loadCategoryItems(this.category.toLowerCase(), true);
    }
  }

  subCategoryChanged(event: MultiSelectChangeEvent) {
    const categoryFilter = event.value as string[];
    // console.log(categoryFilter);
    const newAttributes: number[] = categoryFilter.map(item => Number(item));

    this.filters.update(filters => {
      const updatedFilters = {
        ...filters,
        attributes: newAttributes
      };
      this.paginatorService.setCategoryFilters(updatedFilters); // cache it locally

      // reset paging to first page
      const first = (updatedFilters.page - 1) * updatedFilters.per;
      this.firstSignal.set(first);

      return updatedFilters;
    });

    if (this.category !== undefined) {
      this.loadCategoryItems(this.category.toLowerCase());
    }
  }

  private mapPreviousPriceRangeToNewSliderRanges(originalPriceArray: IPrices[]): void {
    // If we have cached values for the selected slider range we must map those values to the NEW slider ticks
    // if possible. IF lowerbound not found, use 0. If upperbound not found, use last index.
    const priceArray = this.prices();
    const lastPriceIndex = priceArray.length - 1;
    const [oldMinIndex, oldMaxIndex] = this.cachedPriceRangeIndexes;
    var oldMinValue = originalPriceArray[oldMinIndex]?.sliderTick ?? '';
    var oldMaxValue = originalPriceArray[oldMaxIndex]?.sliderTick ?? '';

    if ((oldMinValue == '') && (oldMaxValue == '')) {
      this.initializePriceRange();
      return; // Nothing to map from old to new
    }

    var newMinIndex = priceArray.findIndex(p => p.sliderTick === oldMinValue);
    if (newMinIndex === -1) {
      newMinIndex = 0;
    }

    // special case where we had only 1 range with same Id
    if (oldMinValue === oldMaxValue) {
      newMaxIndex = newMinIndex + 1;
    } else {
      var newMaxIndex = priceArray.findIndex(p => p.sliderTick === oldMaxValue);
      if (newMaxIndex === -1) {
        newMaxIndex = lastPriceIndex;
      }
    }

    this.priceRangeIndexes.set([newMinIndex, newMaxIndex]);
    this.cachedPriceRangeIndexes = [...this.priceRangeIndexes()];

    // Now update the query filter too for next use
    const priceUiArray = this.prices();
    var priceQueryArray: number[] = [];
    for (const opt of this.prices()) {
      // console.log(opt);
      if (priceUiArray.indexOf(opt) >= newMinIndex && priceUiArray.indexOf(opt) <= newMaxIndex) {
        priceQueryArray.push(opt.id);
      }
    }

    this.filters.update(filters => ({
      ...filters,
      prices: priceQueryArray
    }));
  }

  private initializePriceRange(): void {
    const priceArray = this.prices();
    if (priceArray.length > 0) {
      this.priceRangeIndexes.set([0, priceArray.length - 1]); // full range by default
      this.cachedPriceRangeIndexes = [...this.priceRangeIndexes()];
    }
  }

  private initializeLocationSelection(): void {
    const locs = this.locations();
    this.selectedLocations = locs.map(() => false);
    this.cachedSelectedLocations = [...this.selectedLocations];
  }

  // Helpers for left/right text boxes
  getSelectedLowerBound(): string {
    const priceArray = this.prices();
    if (!priceArray.length) return '';
    const [leftIndex] = this.priceRangeIndexes();
    return priceArray[leftIndex]?.sliderTick ?? '';
  }

  getSelectedUpperBound(): string {
    const priceArray = this.prices();
    if (!priceArray.length) return '';
    const [, rightIndex] = this.priceRangeIndexes();
    return priceArray[rightIndex]?.sliderTick ?? '';
  }

  // Slider change handler - prevents handles from crossing or equaling each other
  handleSliderChange(event: any) {
    const values = event.values as [number, number];
    let [leftIndex, rightIndex] = values;

    // console.log('leftIndex: ', leftIndex, 'rightIndex: ', rightIndex);

    // Ensure left handle never reaches or passes right handle
    if (leftIndex >= rightIndex) {
      leftIndex = rightIndex - 1;
    }

    // Ensure right handle never reaches or passes left handle
    if (rightIndex <= leftIndex) {
      rightIndex = leftIndex + 1;
    }

    // special case when the right handle is dragged beyond the left handle, they swap positions and left can now be negative
    if ((leftIndex < 0) && (rightIndex === 0)) {
      leftIndex = 0;
      rightIndex = leftIndex + 1;
    }


    // console.log('adjusted leftIndex: ', leftIndex, 'adjusted rightIndex: ', rightIndex);

    // Update the model with constrained values
    this.priceRangeIndexes.set([leftIndex, rightIndex]);
  }

  // Slider event handler
  handleSlideEnd(event: SliderSlideEndEvent) {
    // console.log('New values:', event.values);
    this.priceRangeIndexes.set(event.values as [number, number]);
    if (this.priceRangeHasChanged()) {
      this.cachedPriceRangeIndexes = [...this.priceRangeIndexes()]; // update the cached values
      this.onPriceRangeChanged();
    }
  }

  priceRangeHasChanged(): boolean {
    const [oldMin, oldMax] = this.cachedPriceRangeIndexes;
    const [newMin, newMax] = this.priceRangeIndexes();

    return oldMin !== newMin || oldMax !== newMax;
  }

  // Called whenever anything in the modal changes
  onAnyFilterChangedExceptPriceRange(): void {

    // Build a filter object based on the current UI state

    var locationQueryArray: number[] = [];
    const uiLocations = this.locations()
      .filter((_loc, i) => this.selectedLocations[i])
      .map(loc => Number(loc.code));
    for (const opt of uiLocations) {
      locationQueryArray.push(opt);
    }

    const uiAdditionalOptions = {
      buyNow: this.selectedBuyNow && !!this.buyNow(),
      onlineNow: this.selectedOnlineNow && !!this.onlineNow(),
      closingSoon: this.selectedClosingSoon && !!this.closingSoon(),
      newItems: this.selectedNewItems && !!this.newItems()
    };

    this.filters.update(filters => ({
      ...filters,
      locations: locationQueryArray,
      isOnlyNew: uiAdditionalOptions.newItems,
      isOnlyClosingSoon: uiAdditionalOptions.closingSoon,
      isOnlyOnline: uiAdditionalOptions.onlineNow,
      isOnlyBuyNow: uiAdditionalOptions.buyNow
    }));


    // reload with updated filters
    if (this.category !== undefined) {
      this.loadCategoryItems(this.category);
    }
  }

  onPriceRangeChanged(): void {

    // Modify the Price filter

    // loop through prices and map selected filters to the query filter
    var [leftIndex, rightIndex] = this.priceRangeIndexes();
    if (leftIndex > rightIndex) {
      // swap them - this means someone slid the right slider beyond the left slider, or visa-versa
      var tmpLeft = leftIndex;
      leftIndex = rightIndex;
      rightIndex = tmpLeft;
    }

    const priceUiArray = this.prices();
    var priceQueryArray: number[] = [];
    for (const opt of this.prices()) {
      // console.log(opt);
      if (priceUiArray.indexOf(opt) >= leftIndex && priceUiArray.indexOf(opt) <= rightIndex) {
        priceQueryArray.push(opt.id);
      }
    }

    this.filters.update(filters => ({
      ...filters,
      prices: priceQueryArray
    }));

    if (this.category !== undefined) {
      this.loadCategoryItems(this.category.toLowerCase(), false, true);
    }
  }
}
